import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, Trash2 } from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { ADMIN_CLIENTS } from '../../constants/testIds';

const STATUS_OPTIONS = ['pending', 'active', 'paused'];

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-stone-200 text-stone-600',
};

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    adminApi
      .get('/admin/clients')
      .then(({ data }) => setClients(data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleStatusChange = async (id, status) => {
    await adminApi.patch(`/admin/clients/${id}`, { status });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this client?')) return;
    await adminApi.delete(`/admin/clients/${id}`);
    load();
  };

  return (
    <div data-testid={ADMIN_CLIENTS.page}>
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-2xl font-extrabold text-stone-900 mb-1">Clients</h1>
          <p className="text-stone-500 text-sm">Everyone you've onboarded, with subscription status.</p>
        </div>
        <Link
          to="/admin/clients/new"
          data-testid={ADMIN_CLIENTS.addClientButton}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] transition"
        >
          <UserPlus size={16} /> Add client
        </Link>
      </div>

      {!loading && clients.length === 0 ? (
        <div data-testid={ADMIN_CLIENTS.emptyState} className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center">
          <p className="text-stone-500 text-sm">No clients yet. Add your first client to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <table data-testid={ADMIN_CLIENTS.table} className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 text-xs uppercase font-semibold">
              <tr>
                <th className="text-left px-5 py-3">Business</th>
                <th className="text-left px-5 py-3">Contact</th>
                <th className="text-left px-5 py-3">Package</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {clients.map((c) => (
                <tr key={c.id} data-testid={ADMIN_CLIENTS.row}>
                  <td className="px-5 py-4 font-semibold text-stone-900">{c.business_name}</td>
                  <td className="px-5 py-4 text-stone-600">{c.contact_email}</td>
                  <td className="px-5 py-4 text-stone-600">{c.package_name}</td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={ADMIN_CLIENTS.statusSelect}
                      value={c.status}
                      onChange={(e) => handleStatusChange(c.id, e.target.value)}
                      className={`text-xs font-semibold rounded-full px-2.5 py-1 border-none focus:outline-none focus:ring-2 focus:ring-[#F97316] ${STATUS_STYLES[c.status] || ''}`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      data-testid={ADMIN_CLIENTS.deleteButton}
                      onClick={() => handleDelete(c.id)}
                      className="text-stone-400 hover:text-red-500 transition"
                      title="Remove client"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminClients;
